// String-valued keys that may directly hold the tool name across the various
// shapes tool calls arrive in (OpenAI, legacy, realtime/voice transfer tools, etc.).
const TOOL_CALL_NAME_STRING_KEYS = ["tool_name", "tool_call", "tool"] as const
const TOOL_CALL_NAME_REGEX_KEYS = [
  ...TOOL_CALL_NAME_STRING_KEYS,
  "function",
] as const
const TOOL_CALL_NAME_PATTERN = new RegExp(
  `(?:${TOOL_CALL_NAME_REGEX_KEYS.join("|")})\\s*[:=]\\s*"?([A-Za-z0-9_.-]+)"?`,
)

/**
 * Best-effort extraction of a tool call's name from any of the shapes
 * `isToolCallContent` accepts. Returns null when no name can be recovered.
 *
 * `parseToolCallContent` only surfaces a name for a subset of shapes and
 * otherwise falls back to the literal "Tool Call"; this digs the name out of
 * the remaining shapes (bare `function`/`tool`/`tool_name` strings, `dtmf_tool`,
 * and the non-JSON `{tool_call: name, arguments: ...}` string) so the name can
 * be surfaced inline when the feature flag is enabled.
 */
export const extractToolCallName = (content: unknown): string | null => {
  if (typeof content === "string") {
    const trimmed = content.trim()
    try {
      return extractToolCallName(JSON.parse(trimmed))
    } catch {
      const match = trimmed.match(TOOL_CALL_NAME_PATTERN)
      return match ? match[1] : null
    }
  }

  if (typeof content === "object" && content !== null) {
    const record = content as Record<string, unknown>

    // OpenAI shape: { function: { name } }
    const toolFunction = record.function
    if (toolFunction && typeof toolFunction === "object") {
      const name = (toolFunction as { name?: unknown }).name
      if (typeof name === "string" && name.trim() !== "") {
        return name
      }
    }
    // function carried as a bare string name
    if (typeof toolFunction === "string" && toolFunction.trim() !== "") {
      return toolFunction
    }

    // dtmf_tool: { name } or a bare string
    const dtmfTool = record.dtmf_tool
    if (dtmfTool && typeof dtmfTool === "object") {
      const name = (dtmfTool as { name?: unknown }).name
      if (typeof name === "string" && name.trim() !== "") {
        return name
      }
    }
    if (typeof dtmfTool === "string" && dtmfTool.trim() !== "") {
      return dtmfTool
    }

    // Common string-valued name keys (tool_name / tool_call / tool)
    for (const key of TOOL_CALL_NAME_STRING_KEYS) {
      const value = record[key]
      if (typeof value === "string" && value.trim() !== "") {
        return value
      }
    }
  }

  return null
}
